import {
  CheckCircleIcon,
  InfoIcon,
  WarningIcon,
  XCircleIcon,
} from "@phosphor-icons/react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

import { cn } from "@/lib/utils"
import { Spinner } from "@/components/ui/spinner"
import { BOTTOM_STACK_GAP } from "@/lib/hooks/useBottomLayers"
import { useTheme } from "@/lib/hooks/useTheme"

import "./sonner.css"

/*
  A toastok a képernyő alsó vermének tetején jelennek meg (`lib/hooks/useBottomLayers.ts`):
  a lebegő sávok felső éle (`--bottom-stack-top`) fölött, egy réssel, vagy ha nincs sáv, a
  Sonner saját alap-távolságán (asztalon 24, 600 px alatt 16 px). Így a nézetváltó egy toast
  miatt sosem mozdul el, és a tömeges műveletek sávját sem takarja el toast. Ez a Sonner
  nyilvános `offset` / `mobileOffset` beállítása, CSS-értékkel: a változó változására a
  böngésző maga igazít, nincs mit mérni.
*/
const STACK_CLEARANCE = `calc(var(--bottom-stack-top, 0px) + ${BOTTOM_STACK_GAP}px)`
const DEFAULT_OFFSET = { bottom: `max(24px, ${STACK_CLEARANCE})` }
const DEFAULT_MOBILE_OFFSET = { bottom: `max(16px, ${STACK_CLEARANCE})` }

const Toaster = ({
  theme,
  className,
  gap = 12,
  icons,
  style,
  offset = DEFAULT_OFFSET,
  mobileOffset = DEFAULT_MOBILE_OFFSET,
  ...props
}: ToasterProps) => {
  const { theme: appTheme } = useTheme()

  return (
    <Sonner
      {...props}
      theme={theme ?? appTheme}
      gap={gap}
      offset={offset}
      mobileOffset={mobileOffset}
      className={cn("app-toaster", className)}
      icons={{
        success: <CheckCircleIcon />,
        info: <InfoIcon />,
        warning: <WarningIcon />,
        error: <XCircleIcon />,
        loading: <Spinner />,
        ...icons,
      }}
      style={
        {
          "--width": "460px",
          "--normal-bg": "color-mix(in oklab, var(--card) 80%, transparent)",
          "--normal-text": "var(--card-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "min(2rem, calc(var(--radius) + 0.75rem))",
          ...style,
        } as React.CSSProperties
      }
    />
  )
}

export { Toaster }
